import Carousel from "@stimulus-components/carousel";

export default class extends Carousel {
  static targets = ["slideButton"];

  connect() {
    super.connect();

    this.swiper;

    this.defaultOptions;

    setTimeout(() => {
      this.setupCustomNavigation();
      this.fixInitialSlideState();
      this.setupScrollSync();
    }, 100);
  }

  setupCustomNavigation() {
    if (this.getNavigation() && this.swiper) {
      const prevButton = this.element.querySelector(".swiper-button-prev");
      const nextButton = this.element.querySelector(".swiper-button-next");

      if (prevButton) {
        prevButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          this.handlePrevClick();
        });
      }

      if (nextButton) {
        nextButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          this.handleNextClick();
        });
      }

      this.updateNavigationState();

      this.swiper.on("slideChange", () => {
        this.updateNavigationState();
      });
    }
  }

  handlePrevClick() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  handleNextClick() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  updateNavigationState() {
    if (!this.swiper) return;

    const carouselMode = this.getCarouselMode();
    const isFadeMode = carouselMode === "fade";

    const prevButton = this.element.querySelector(".swiper-button-prev");
    const nextButton = this.element.querySelector(".swiper-button-next");

    // Don't disable buttons if loop is enabled (fade mode)
    if (isFadeMode) {
      if (prevButton) {
        prevButton.classList.remove("swiper-button-disabled");
        prevButton.style.opacity = "1";
        prevButton.style.cursor = "pointer";
      }
      if (nextButton) {
        nextButton.classList.remove("swiper-button-disabled");
        nextButton.style.opacity = "1";
        nextButton.style.cursor = "pointer";
      }
      return;
    }

    if (prevButton) {
      if (this.swiper.isBeginning) {
        prevButton.classList.add("swiper-button-disabled");
        prevButton.style.opacity = "0.5";
        prevButton.style.cursor = "not-allowed";
      } else {
        prevButton.classList.remove("swiper-button-disabled");
        prevButton.style.opacity = "1";
        prevButton.style.cursor = "pointer";
      }
    }

    if (nextButton) {
      if (this.swiper.isEnd) {
        nextButton.classList.add("swiper-button-disabled");
        nextButton.style.opacity = "0.5";
        nextButton.style.cursor = "not-allowed";
      } else {
        nextButton.classList.remove("swiper-button-disabled");
        nextButton.style.opacity = "1";
        nextButton.style.cursor = "pointer";
      }
    }
  }

  get defaultOptions() {
    const carouselMode = this.getCarouselMode();
    const isFadeMode = carouselMode === "fade";

    const baseOptions = {
      slidesPerView: isFadeMode ? 1 : this.getSlidesPerView(),
      spaceBetween: this.getSpaceBetween(),
      navigation: false,
      pagination: this.getPagination()
        ? {
            el: ".swiper-pagination",
            clickable: true,
          }
        : false,
      cssMode: this.getCssMode(),
    };

    if (isFadeMode) {
      baseOptions.effect = "fade";
      baseOptions.fadeEffect = {
        crossFade: true,
      };
      baseOptions.loop = true;
      baseOptions.on = {
        init: () => {
          this.fixInitialSlideState();
        },
      };
    }

    if (!isFadeMode) {
      baseOptions.breakpoints = this.getBreakpoints();
    }

    return baseOptions;
  }

  getCarouselMode() {
    const dataCarouselMode = this.element.dataset.carouselMode;
    return dataCarouselMode || "default";
  }

  getCssMode() {
    const cssMode = this.element.dataset.cssMode;
    return cssMode === "true";
  }

  /**
   * Determine breakpoints for this carousel.
   * Priority:
   *   1. `data-breakpoints` JSON on element
   *   2. Default {0:1.3, 620:2, 860:3}
   */
  getBreakpoints() {
    const raw = this.element.dataset.breakpoints;
    const gap = this.getSpaceBetween();

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        Object.keys(parsed).forEach((k) => {
          const v = parsed[k];
          if (typeof v === "number") {
            parsed[k] = { slidesPerView: v, spaceBetween: gap };
          } else if (typeof v === "object") {
            if (v.slidesPerView === undefined) v.slidesPerView = 1;
            if (v.spaceBetween === undefined) v.spaceBetween = gap;
          }
        });
        return parsed;
      } catch (e) {
        console.warn("[Carousel] invalid data-breakpoints JSON", e);
      }
    }

    return {
      0: { slidesPerView: 1.3, spaceBetween: gap },
      620: { slidesPerView: 2, spaceBetween: gap },
      860: { slidesPerView: 3, spaceBetween: gap },
    };
  }

  getSlidesPerView() {
    const dataSlidesPerView = this.element.dataset.slidesPerView;
    if (dataSlidesPerView) {
      return parseFloat(dataSlidesPerView);
    }

    const element = this.element.closest(".slider__wrapper") || this.element;
    const perPage = getComputedStyle(element).getPropertyValue("--per_page");
    return perPage ? parseFloat(perPage) : 5;
  }

  getSpaceBetween() {
    const dataSpaceBetween = this.element.dataset.spaceBetween;
    if (dataSpaceBetween) {
      return parseInt(dataSpaceBetween);
    }

    const element = this.element.closest(".slider__wrapper") || this.element;
    const gap = getComputedStyle(element).getPropertyValue("--gap");
    return gap ? parseInt(gap) : 24;
  }

  getNavigation() {
    const dataNavigation = this.element.dataset.navigation;
    return dataNavigation === "true";
  }

  getPagination() {
    const dataPagination = this.element.dataset.pagination;
    return dataPagination === "true";
  }

  fixInitialSlideState() {
    const carouselMode = this.getCarouselMode();
    const isFadeMode = carouselMode === "fade";

    if (isFadeMode && this.swiper) {
      const slides = this.element.querySelectorAll(".swiper-slide");
      slides.forEach((slide, index) => {
        if (index === 0) {
          slide.classList.add("swiper-slide-active");
        } else {
          slide.classList.remove("swiper-slide-active");
        }
      });
    }
  }

  setupScrollSync() {
    if (!this.swiper || this.getCssMode()) return;

    const carouselMode = this.getCarouselMode();
    const isFadeMode = carouselMode === "fade";

    if (isFadeMode) return;

    const carouselWrapper = this.element;

    const handleScroll = () => {
      this.updateNavigationStateFromScroll();
    };

    carouselWrapper.addEventListener("scroll", handleScroll, { passive: true });

    this.swiper.on("slideChange", () => {
      this.updateNavigationStateFromScroll();
    });

    this.updateNavigationStateFromScroll();
  }

  updateNavigationStateFromScroll() {
    if (!this.swiper) return;

    const carouselMode = this.getCarouselMode();
    const isFadeMode = carouselMode === "fade";

    if (isFadeMode) {
      this.updateNavigationState();
      return;
    }

    const prevButton = this.element.querySelector(".swiper-button-prev");
    const nextButton = this.element.querySelector(".swiper-button-next");

    if (!prevButton || !nextButton) return;

    const carouselWrapper = this.element;
    const scrollLeft = carouselWrapper.scrollLeft;
    const scrollWidth = carouselWrapper.scrollWidth;
    const clientWidth = carouselWrapper.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    const isAtBeginning = scrollLeft <= 5;
    const isAtEnd = scrollLeft >= maxScroll - 5;

    if (isAtBeginning) {
      prevButton.classList.add("swiper-button-disabled");
      prevButton.style.opacity = "0.5";
      prevButton.style.cursor = "not-allowed";
      prevButton.style.pointerEvents = "none";
    } else {
      prevButton.classList.remove("swiper-button-disabled");
      prevButton.style.opacity = "1";
      prevButton.style.cursor = "pointer";
      prevButton.style.pointerEvents = "auto";
    }

    if (isAtEnd) {
      nextButton.classList.add("swiper-button-disabled");
      nextButton.style.opacity = "0.5";
      nextButton.style.cursor = "not-allowed";
      nextButton.style.pointerEvents = "none";
    } else {
      nextButton.classList.remove("swiper-button-disabled");
      nextButton.style.opacity = "1";
      nextButton.style.cursor = "pointer";
      nextButton.style.pointerEvents = "auto";
    }
  }
}
